import { useQuery, queryOptions } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";

type PayerListItem = {
  id: string;
  displayName: string;
  name?: string;
  createdAt?: string | null;
};

/**
 * Query key factory for payer-related queries.
 */
export const payersKeys = {
  all: ["payers"] as const,
  list: () => ["payers"] as const,
  search: (search: string) => ["payers-search", search] as const,
};

export type Payer = {
  id: string;
  name: string;
  domain: "fertility" | null;
  tpaFlag: boolean;
  phone: string | null;
  productLines: string[] | null;
  portalUrl: string | null;
  ediReceiverId: string | null;
  clearinghouseSupported: boolean | null;
  notes: string | null;
  createdAt: string;
  // For display purposes (use name if displayName not available)
  displayName?: string;
};

function toPayer(p: PayerListItem): Payer {
  const display = p.displayName ?? p.name ?? "";
  return {
    id: p.id,
    name: p.name ?? display,
    displayName: display,
    domain: null,
    tpaFlag: false,
    phone: null,
    productLines: null,
    portalUrl: null,
    ediReceiverId: null,
    clearinghouseSupported: null,
    notes: null,
    createdAt: p.createdAt ?? new Date().toISOString(),
  };
}

/**
 * Hook to fetch all payers (for initial load and matching)
 */
export function usePayers() {
  const { client, tenantId } = useApiClient();

  return useQuery({
    queryKey: payersKeys.list(),
    queryFn: async () => {
      const { data, error, response } = await client.GET("/payers", {});

      if (error) {
        throwApiError(
          error,
          "Failed to fetch payers",
          "usePayers",
          response?.status,
        );
      }

      return (data?.data ?? []).map(toPayer);
    },
    // Path no longer carries tenantId, but its presence is a proxy for
    // an authenticated session — avoid firing the query pre-auth.
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Query options for searching payers via the modern /payers/search endpoint.
 *
 * Searches Stedi-synced payer data; not scoped to a specific patient.
 */
export function usePayersSearchQueryOptions(search: string) {
  const { client, tenantId } = useApiClient();

  return queryOptions({
    queryKey: payersKeys.search(search),
    queryFn: async () => {
      if (!search.trim()) return [];

      const { data, error, response } = await client.GET("/payers/search", {
        params: { query: { q: search } },
      });

      if (error) {
        throwApiError(
          error,
          "Failed to search payers",
          "usePayersSearch",
          response?.status,
        );
      }

      return (data?.data ?? []).map(toPayer);
    },
    // Same auth-presence guard as usePayers().
    enabled: !!tenantId && search.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook wrapper around usePayersSearchQueryOptions for convenience.
 * Accepts a search string and returns a standard TanStack Query result.
 */
export function usePayersSearch(search: string) {
  const options = usePayersSearchQueryOptions(search);
  return useQuery(options);
}
